/**
 * 金额转中文
 * 思路：
 *                              个
 *      十     百      千       万
 *      十万   百万    千万     亿
 *      十亿   百亿    千亿
 *
 *                              1
 *      2      3       4        5
 *      6      7       8        9
 *      10
 *
 * 计算步骤
 * 1. 获取当前数值大小
 * 2. 排除个位后 数值按个，十，百，千有规律的重复 所以计算其和4的余数 pos % 4
 * 3. pos = 0 ~ 3 没有最大单位
 *    pos = 4 ~ 7 最大单位是万
 *    pos = 8 ~ 11 最大单位是亿
 * pos / 4 的整数就是最大单位
 *
 */
export function getAmountChinese(val: number) {
  if (!val && val !== 0) return ''
  const amount = +val
  if (Number.isNaN(amount) || amount < 0) return ''
  const NUMBER = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
  const N_UNIT1 = ['', '拾', '佰', '仟']
  const N_UNIT2 = ['', '万', '亿', '兆']
  const D_UNIT = ['角', '分', '厘', '毫']
  const [integer, decimal] = amount.toString().split('.')
  if (integer && integer.length > 15) return '数字较大溢出'
  let res = ''
  // 整数部分
  if (integer) {
    for (let i = 0, len = integer.length; i < len; i++) {
      const num: any = integer.charAt(i)
      const pos = len - i - 1 // 排除个位后 所处的索引位置
      if (num === '0') {
        // 当前位 等于 0 且下一位也等于 0 则可跳过计算
        if (i === len - 1) {
          if (integer.length === 1) res += '零' // 0.35 这种情况不可跳过计算
          break
        }
        if (integer.charAt(i + 1) === '0') continue
      }
      res += NUMBER[num]
      if (parseInt(num)) res += N_UNIT1[pos % 4]
      if (pos % 4 === 0) res += N_UNIT2[Math.floor(pos / 4)]
    }
  }
  res += '元'
  // 小数部分
  if (parseInt(decimal)) {
    for (let i = 0; i < 4; i++) {
      const num: any = decimal.charAt(i)
      if (parseInt(num)) res += NUMBER[num] + D_UNIT[i]
    }
  } else {
    res += '整'
  }
  return res
}

// 转千位分隔
export function thousandsFormat(num: string | number) {
  if (num === 0) return '0'
  if (!num) return ''
  const numArr = num.toString().split('.')
  numArr[0] = numArr[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return numArr.join('.')
}

// 获取时间单位
export function getDateTimeUnit(format: string) {
  if (format == 'YYYY') return 'year'
  if (format == 'YYYY-MM') return 'month'
  if (format == 'YYYY-MM-DD') return 'day'
  if (format == 'YYYY-MM-DD HH:mm') return 'minute'
  return 'day'
}
/**
 * 将用户输入的连续单个数字合并为一个数
 * @param {Array} expressions - 记录计算表达式的数组
 * @returns {Array} 新的数组
 */
export const mergeNumberOfExps = (expressions: Array<any>): Array<any> => {
  const res: any = []

  const isNumChar = (n: string) => /^[\d.]$/.test(n)
  for (let i = 0; i < expressions.length; i++) {
    if (i > 0 && isNumChar(expressions[i - 1]) && isNumChar(expressions[i])) {
      res[res.length - 1] += expressions[i]
      continue
    }
    res.push(expressions[i])
  }

  return res
}
// /**
//  * 校验表达式是否符合计算法则
//  * @param {Array} expressions - 合并数字后的表达式数组
//  * @returns {Boolean}
//  */
// export const validExp = (expressions, mergeNum = true) => {
//   const temp = mergeNum ? mergeNumberOfExps(expressions) : expressions
//   const arr = temp.filter((t) => !'()'.includes(t))
//   // 去括号后 length应该为奇数  并且第一个字符和最后一个字符应该为数字而非计算符号
//   if (
//     temp.length % 2 === 0 ||
//     arr.length % 2 === 0 ||
//     Number.isNaN(+arr[0]) ||
//     Number.isNaN(+arr[arr.length - 1])
//   ) {
//     return false
//   }
//   for (let i = 0; i < arr.length - 1; i += 2) {
//     if (typeof +arr[i] !== 'number' || !Number.isNaN(+arr[i + 1])) return false
//   }
//   return true
// }
/**
 * 中缀转后缀（逆波兰 Reverse Polish Notation）
 * @param {Array} exps - 中缀表达式数组
 */
export const toRPN = (exps: Array<any>) => {
  const s1: any[] = [] // 符号栈
  const s2: any[] = [] // 输出栈
  const getTopVal = (stack: string | any[]) =>
    stack.length > 0 ? stack[stack.length - 1] : null
  const levelCompare = (c1: any, c2: string) => {
    const getIndex = (c: string) =>
      ['+-', '×÷', '()'].findIndex((t) => t.includes(c))
    return getIndex(c1) - getIndex(c2)
  }
  exps.forEach((t) => {
    if (typeof t === 'string' && Number.isNaN(Number(t))) {
      // 是符号
      if (t === '(') {
        s1.push(t)
      } else if (t === ')') {
        let popVal
        do {
          popVal = s1.pop()
          popVal !== '(' && s2.push(popVal)
        } while (s1.length && popVal !== '(')
      } else {
        let topVal = getTopVal(s1)
        if (!topVal) {
          // s1 为空 直接push
          s1.push(t)
        } else {
          while (topVal && topVal !== '(' && levelCompare(topVal, t) >= 0) {
            // 优先级 >= t 弹出到s2
            s2.push(s1.pop())
            topVal = getTopVal(s1)
          }
          s1.push(t)
        }
      }
      return
    }
    s2.push(t) // 数字直接入栈
  })
  while (s1.length) {
    s2.push(s1.pop())
  }
  return s2
}
// /**
//  * 计算后缀表达式的值
//  * @param {Array} rpnExps - 后缀表达式
//  */
export const calcRPN = (rpnExps: any[]) => {
  rpnExps = rpnExps.concat()
  const calc = (x: any, y: any, type: any) => {
    const a1 = Number(x),
      a2 = Number(y)
    switch (type) {
      case '+':
        return a1 + a2
      case '-':
        return a1 - a2
      case '×':
        return a1 * a2
      case '÷':
        return a1 / a2
    }
  }
  for (let i = 2; i < rpnExps.length; i++) {
    if ('+-×÷'.includes(rpnExps[i])) {
      const val = calc(rpnExps[i - 2], rpnExps[i - 1], rpnExps[i])
      rpnExps.splice(i - 2, 3, val)
      i = i - 2
    }
  }
  return rpnExps[0]
}
